import {
    AzureBlobFSLinkedService,
    Collection,
    DelimitedTextDataset,
    DeltaDataset,
    FileServerLinkedService,
    FormatDatasetV2,
    HdfsLinkedService,
    IAttribute,
    IModel,
    IValidationContext,
    IViolation,
    LakehouseLinkedService,
    ModelConstants,
    OneLakeFileLinkedService,
    TabularTypeDataset,
    stringIsNullOrEmpty
} from 'shell-models-lib';
import { Metamodel } from 'app/model/base/model';
import { Activity } from './activity';

import { Dataset } from 'app/model/dataset/dataset';
import { ParameterValue } from 'app/model/base/parameter-value';
import { ParameterConfig } from 'app/model/pipeline/parameter-config';
import {
    validateEntityParameters,
    validateActivitySupportability,
    validateCompressionForDelimitedText,
    validateDelimiterForDelimitedText,
    validateManagedVNetIRStatus
} from 'app/shared/utils/activity-validation.util';
import { FileTypeDataset } from 'app/model/dataset/file-type.dataset';
import { containWilcard, format } from 'app/shared/utils/string.util';
import { StoreReadSettings } from './copysource/store-read-settings/store-read-settings';
import { FormatReadSettings } from './copysource/format-read-settings/format-read-settings';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { isBinaryTypeDataset } from 'app/shared/utils/dataset-validation.util';
import { XmlDataset } from 'app/model/dataset/xml.dataset';
import { AzureBlobDataset } from 'app/model/dataset/azure-blob.dataset';
import { AzureBlobStorageLinkedService } from 'app/model/linkedservice/azure-blob-storage.linkedservice';
import { AzureDataLakeStoreCosmosStructuredStreamFileDataset } from 'app/model/dataset/azure-data-lake-store-cosmos-structured-stream.dataset';
import { AzureDataLakeStoreGen2CosmosStructuredStreamFileDataset } from 'app/model/dataset/azure-data-lake-store-gen2-cosmos-structured-stream.dataset';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';

export type GetMetaDataActionOutputFieldNames =
    | 'structure'
    | 'columnCount'
    | 'exists'
    | 'itemName'
    | 'itemType'
    | 'lastModified'
    | 'childItems'
    | 'size'
    | 'contentMD5'
    | 'created'
    | 'partitionIndexes';

@Metamodel.model({
    name: ModelConstants.getMetadataActivity,
    display: [
        format(stringResources.ModelName_ActivityTemplate, stringResources.GetMetadataActivityLabel),
        format(stringResources.ModelName_ActivitiesTemplate, stringResources.GetMetadataActivityLabel)
    ],
    referable: true
})
export class GetMetadataActivity extends Activity {
    public static readonly MODEL: IModel;
    public static readonly FIELD_LIST: IAttribute;

    /**
     * Lists the types of metadata information required.
     */
    @Metamodel.attribute({
        display: stringResources.GetMetadataActivityConfigurationFieldList,
        collection: Collection.Array,
        association: 'FIELD_LIST',
        constraint: {
            validator: GetMetadataActivity.validateFieldList
        },
        templatable: true
    })
    public fieldList?: ParameterConfig[];

    @Metamodel.attribute({
        display: !__IS_FABRIC_DPE__ ? stringResources.ModelName_DataSet : stringResources.ConnectionLabel,
        constraint: {
            required: true,
            validator: GetMetadataActivity.validateSourceDataset
        },
        reference: (context) => !context?.inlineDataset,
        type: Dataset.MODEL
    })
    public dataset?: Dataset;

    /**
     * Parameter value for referenced dataset.
     */
    @Metamodel.attribute({
        collection: Collection.Array,
        constraint: {
            required: false,
            validator: GetMetadataActivity.validateDatasetParameters
        }
    })
    public datasetParameters: ParameterValue[] = [];

    // for dataset v2
    @Metamodel.attribute({
        display: stringResources.ModelName_StoreReadSettings,
        type: StoreReadSettings.MODEL
    })
    public storeSettings: StoreReadSettings;

    // for dataset v2
    @Metamodel.attribute({
        display: stringResources.ModelName_FormatReadSettings,
        type: FormatReadSettings.MODEL
    })
    public formatSettings: FormatReadSettings;

    constructor(name?: string, description?: string) {
        super(name, description);
        this.activityType = 'ExecutionActivity';
    }

    private static validateDatasetParameters(activity: GetMetadataActivity): IViolation[] | undefined {
        if (activity != null && activity.dataset != null) {
            return validateEntityParameters(activity.dataset, activity.datasetParameters || [], activity);
        }
        return undefined;
    }

    private static async validateFieldList(activity: GetMetadataActivity): Promise<IViolation[] | undefined> {
        if (activity.fieldList) {
            // value is invalid only when both value and templated value are null
            if (
                activity.fieldList.filter(
                    (field) => field && (field.value != null || ParameterConfig.VALUE.templateValue(field) != null)
                ).length === 0
            ) {
                return [
                    {
                        message: () => stringResources.GetMetadataActivityFieldListEmpty
                    }
                ];
            }
        }
        return undefined;
    }

    private static async validateSourceDataset(
        activity: GetMetadataActivity,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        _value: any,
        _violations: IViolation,
        context: IValidationContext
    ): Promise<IViolation[] | undefined> {
        if (activity == null || activity.dataset == null) {
            return undefined;
        }
        const dataset = activity.dataset;
        let result: IViolation[] = [];
        const irStatusViolations = await validateManagedVNetIRStatus(context, activity.dataset);
        if (irStatusViolations != null) {
            result = result.concat(irStatusViolations);
        }
        if (dataset instanceof FileTypeDataset) {
            const attr = Metamodel.modelOf(dataset).attribute('wildcardPath');
            if (dataset.wildcardPath != null || attr.templateValue(dataset)) {
                return [
                    {
                        message: () => stringResources.GetMetadataActivityDatasetWildcardValidationError,
                        redirectTo: {
                            entity: dataset,
                            attribute: Metamodel.modelOf(dataset).attribute('wildcardPath')
                        }
                    }
                ];
            }
            if (containWilcard(dataset.folderPath)) {
                result.push({
                    message: () => stringResources.GetMetadataActivityDatasetWildcardValidationFolderPathError,
                    redirectTo: {
                        entity: dataset,
                        attribute: Metamodel.modelOf(dataset).attribute('folderPath')
                    }
                });
            }
            if (containWilcard(dataset.fileName)) {
                result.push({
                    message: () => stringResources.GetMetadataActivityDatasetWildcardValidationFileNameError,
                    redirectTo: {
                        entity: dataset,
                        attribute: Metamodel.modelOf(dataset).attribute('fileName')
                    }
                });
            }
        }
        // validate DelimitedTextDataset
        if (dataset instanceof DelimitedTextDataset) {
            // in copy activity, lookup activity and get metadata activity, rowDelimiter and columnDelimiter cannot be empty string or multi-char string
            result.push(...(await validateDelimiterForDelimitedText(dataset, activity)));
            result.push(...(validateCompressionForDelimitedText(dataset, activity) || []));
        }

        if (
            __IS_FABRIC_DPE__ &&
            dataset instanceof TabularTypeDataset &&
            stringIsNullOrEmpty(TabularTypeDataset.TABLE.templateValue(activity.dataset)) &&
            stringIsNullOrEmpty(dataset.table) &&
            stringIsNullOrEmpty(TabularTypeDataset.TABLE_NAME.templateValue(activity.dataset)) &&
            stringIsNullOrEmpty(dataset.tableName)
        ) {
            result.push({
                message: () =>
                    format(
                        stringResources.RequiredValidatorMessageNoVowel,
                        stringResources.ModelName_Table,
                        stringResources.GetMetadataActivityLabel,
                        stringResources.Activity.toLowerCase()
                    ),
                messageType: 'missing',
                redirectTo: {
                    entity: dataset,
                    attribute: Metamodel.modelOf(dataset).attribute('tableName')
                }
            });
        }

        if (dataset && dataset.linkedService) {
            const linkedServiceRegistry = await getServiceInstance<LinkedServiceRegistryService>(
                context.contextData,
                'LinkedServiceRegistryService'
            );
            const lsModel = Metamodel.modelByName(dataset.getLinkedServiceType());
            const metadata = linkedServiceRegistry.getLinkedServiceConfiguration(lsModel.name);
            if (!metadata.supportGetMetadataActivity) {
                result.push({
                    message: () =>
                        format(
                            stringResources.ActivityNotSupportLinkedServiceError,
                            GetMetadataActivity.MODEL.display[0],
                            __IS_FABRIC_DPE__ ? metadata.displayName : lsModel.display[0],
                        ),
                    messageType: 'unsupported',
                    redirectTo: {
                        entity: dataset,
                        attribute: Metamodel.modelOf(dataset).attribute('linkedService')
                    }
                });
            }
        }
        // validate dynamic datasets
        const activitySupportabilityViolations = await validateActivitySupportability(activity, dataset, true, context);
        if (activitySupportabilityViolations != null && activitySupportabilityViolations.length > 0) {
            result = result.concat(activitySupportabilityViolations);
        }
        if (result.length > 0) {
            return result;
        }
        return undefined;
    }

    public getFieldNameSet(): Set<GetMetaDataActionOutputFieldNames> {
        const dataset = this.dataset;
        let result: GetMetaDataActionOutputFieldNames[] = [];
        if (dataset) {
            // tabular
            if (dataset instanceof TabularTypeDataset) {
                result.push('structure', 'columnCount', 'exists');
            } else if (dataset instanceof DeltaDataset) {
                result.push('exists', 'columnCount', 'structure');
            } else {
                // file type
                result.push('itemName', 'itemType', 'lastModified', 'exists');
                const linkedService = dataset.linkedService;
                if (this.isFolderDataset()) {
                    // folder
                    result.push('childItems');
                }
                if (this.isFileDataset()) {
                    // file
                    if (isBinaryTypeDataset(dataset)) {
                        result.push('size');
                    } else if (dataset instanceof XmlDataset) {
                        result.push('size', 'columnCount');
                    } else {
                        result.push('size', 'structure', 'columnCount');
                    }
                    if (
                        dataset instanceof AzureBlobDataset ||
                        linkedService instanceof AzureBlobStorageLinkedService ||
                        linkedService instanceof AzureBlobFSLinkedService ||
                        linkedService instanceof LakehouseLinkedService ||
                        linkedService instanceof OneLakeFileLinkedService
                    ) {
                        // blob, blob fs, & LH file support contentMD5
                        result.push('contentMD5');
                    }
                }
                if (linkedService instanceof FileServerLinkedService) {
                    result.push('created');
                }
                if (linkedService instanceof HdfsLinkedService) {
                    result = []; // not support hdfs linked service
                }
                if (
                    dataset instanceof AzureDataLakeStoreCosmosStructuredStreamFileDataset ||
                    dataset instanceof AzureDataLakeStoreGen2CosmosStructuredStreamFileDataset
                ) {
                    result.push('partitionIndexes');
                }
            }
        }
        return new Set<GetMetaDataActionOutputFieldNames>(result);
    }

    public isFolderDataset(): boolean {
        const dataset = this.dataset;
        if (dataset == null) {
            return false;
        }
        if (dataset instanceof FileTypeDataset) {
            return !dataset.fileName;
        }
        if (dataset instanceof FormatDatasetV2) {
            const loc = dataset.location;
            return loc && loc.isFolder(false);
        }
        return false;
    }

    public isFileDataset(): boolean {
        const dataset = this.dataset;
        if (dataset == null) {
            return false;
        }
        if (dataset instanceof FileTypeDataset) {
            return !!(dataset.fileName || FileTypeDataset.FILE_NAME.templateValue(dataset));
        }
        if (dataset instanceof FormatDatasetV2) {
            const loc = dataset.location;
            return !(loc && loc.isFolder(true));
        }
        return false;
    }

    public onActivityCloned(clonedActivity: GetMetadataActivity, context: { inlineDataset?: boolean }): void {
        if (this.dataset != null) {
            this.dataset.onDatasetCloned(clonedActivity.dataset, context);
        }
    }
}
